/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { type MouseEvent, useState } from 'react';
import {
    Button,
    ButtonGroup,
    Icon,
    Tooltip,
    Placeholder,
    SecurityHelper,
    Heading,
} from 'sarsaparilla';
import { getPassImage } from 'shared-ui/utils/pass-images';
import { useAuthModal } from 'shared-ui/auth';
import type { PassType, PassDetails } from '../../api/interagency-pass-types';
import { formatPassTitle } from '../../utils/pass-text-utils';

import './InteragencyPassCard.scss';

interface InteragencyPassCardProps {
    pass: PassDetails;
    isSitePass?: boolean;
    addToCartEnabled?: boolean;
    overrideAtbPassYear?: number;
    isLoading?: boolean;
    gaTrackingId?: string;
    gaTrackingValue?: string;
    imageSize?: 'md' | 'lg';
    onLearnMoreClick?: () => void;
}

const DISCOUNT_PASS_TYPES = ['access', 'senior-lifetime', 'senior-annual'];

const DISCOUNT_PASS_NAMES: Partial<Record<PassType, string>> = {
    access: 'Access Pass',
    'senior-lifetime': 'Senior Lifetime Pass',
    'senior-annual': 'Senior Annual Pass',
};

const BROWSER_ONLY_PASS_TYPES = [
    'military-lifetime',
    'military-annual',
    'senior-lifetime',
    'senior-annual',
];

function getDiscountTooltip(passType: PassType): string {
    const name = DISCOUNT_PASS_NAMES[passType] ?? 'pass';
    return `The ${name} may offer discounts on expanded amenity fees such as camping, parking, special tours, special permits, or ferries. However, lands managed by concessioners may not accept the ${name}. (Concessioners are private companies granted permission by the agencies to provide services.)`;
}

function InteragencyPassCard({
    pass,
    isSitePass,
    addToCartEnabled,
    overrideAtbPassYear,
    isLoading = false,
    gaTrackingId,
    gaTrackingValue,
    imageSize = 'md',
    onLearnMoreClick,
}: InteragencyPassCardProps) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const { ensureLoggedIn } = useAuthModal();

    const handleImageLoad = () => {
        setImageLoaded(true);
    };
    // Handle loading state for entire component
    if (isLoading) {
        return (
            <div className="pass-result-card pass-result-card--loading">
                <div className="pass-result-card__image-container">
                    <Placeholder
                        className="pass-result-card__image-skeleton"
                        height={imageSize === 'lg' ? '202px' : '160px'}
                    />
                </div>
                <div className="pass-result-card__body">
                    <Placeholder
                        className="pass-result-card__title-skeleton mb-2"
                        height="24px"
                        width="75%"
                    />
                    <ul className="pass-result-card__bullets">
                        <li className="pass-result-card__bullet-skeleton">
                            <Placeholder height="18px" width="80%" />
                        </li>
                        <li className="pass-result-card__bullet-skeleton">
                            <Placeholder height="18px" width="85%" />
                        </li>
                        <li className="pass-result-card__bullet-skeleton">
                            <Placeholder height="18px" width="70%" />
                        </li>
                    </ul>
                </div>
            </div>
        );
    }

    if (isSitePass) {
        return (
            <div className="pass-result-card pass-result-card--site-pass">
                <div className="pass-result-card__body">
                    <div className="pass-result-card__title-row">
                        <Icon
                            iconName="card"
                            className="pass-result-card__title-icon"
                            size="md"
                        />
                        <Heading
                            appearance="h3"
                            headingLevel={3}
                            className="pass-result-card__title"
                        >
                            Site Pass
                        </Heading>
                    </div>
                    <ul className="pass-result-card__bullets">
                        <li>
                            Covers entrance or standard amenity (day-use) fees at a single
                            federal recreation location
                        </li>
                        <li>
                            Price varies based on location and pass type (daily, weekly,
                            seasonal, etc.)
                        </li>
                    </ul>
                    <div className="pass-result-card__recommended">
                        <p className="pass-result-card__recommended-title">
                            Recommended for:
                        </p>
                        <div className="pass-result-card__recommended-tags">
                            <span className="pass-result-card__recommended-tag">
                                Visiting a specific federal recreation location
                            </span>
                            <span className="pass-result-card__recommended-tag">
                                Single visit or yearly passes available
                            </span>
                        </div>
                    </div>
                    <div className="pass-result-card__actions">
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                href="#site-specific-digital-passes"
                                onClick={onLearnMoreClick}
                                gaTrackingId={gaTrackingId}
                                gaTrackingValue={gaTrackingValue}
                            >
                                Learn More
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            </div>
        );
    }

    const imageUrl = getPassImage({
        passType: pass.type,
        variant: { type: 'sized', size: imageSize },
        ldYearOverride: overrideAtbPassYear ? String(overrideAtbPassYear) : undefined,
    });

    const title = formatPassTitle(pass.title, pass.type, overrideAtbPassYear);
    const providesDiscounts = DISCOUNT_PASS_TYPES.includes(pass.type);

    let addToCartTitle = 'Add to Cart';
    // only some ATB pass types can be purchased in mobile apps
    if (SecurityHelper.isMobileApp() && BROWSER_ONLY_PASS_TYPES.includes(pass.type)) {
        addToCartTitle = 'Purchase on Browser';
    }

    const encodedPassType = encodeURIComponent(pass.type);
    const passDetailsUrl = `/interagency-pass/types/${encodedPassType}`;
    const orderDetailsUrl = `/interagency-pass/order-details/${encodedPassType}`;

    const handleAddToCartClick = async (event: MouseEvent<HTMLAnchorElement>) => {
        if (SecurityHelper.isLoggedIn()) {
            return;
        }

        event.preventDefault();
        const didLogin = await ensureLoggedIn();

        if (didLogin) {
            window.location.assign(orderDetailsUrl);
        }
    };

    return (
        <div className="pass-result-card">
            {providesDiscounts && (
                <span className="pass-result-card__discount-badge">
                    PROVIDES DISCOUNTS
                </span>
            )}
            <a
                href={passDetailsUrl}
                className="pass-result-card__image-container"
                data-ga-tracking-id={gaTrackingId}
                data-ga-tracking-value={
                    gaTrackingValue ? `${gaTrackingValue} - Image` : undefined
                }
            >
                {!imageLoaded && (
                    <Placeholder
                        className="pass-result-card__image-skeleton"
                        height={imageSize === 'lg' ? '202px' : '160px'}
                    />
                )}
                <img
                    src={imageUrl}
                    alt={`Learn more about ${pass.title}`}
                    className={`pass-result-card__image ${
                        imageLoaded
                            ? 'pass-result-card__image--loaded'
                            : 'pass-result-card__image--hidden'
                    }`}
                    onLoad={handleImageLoad}
                />
            </a>
            <div className="pass-result-card__body">
                {!imageLoaded ? (
                    <>
                        <Placeholder
                            className="pass-result-card__title-skeleton mb-2"
                            height="24px"
                            width="75%"
                        />
                        <ul className="pass-result-card__bullets">
                            <li className="pass-result-card__bullet-skeleton">
                                <Placeholder height="18px" width="80%" />
                            </li>
                            <li className="pass-result-card__bullet-skeleton">
                                <Placeholder height="18px" width="85%" />
                            </li>
                            <li className="pass-result-card__bullet-skeleton">
                                <Placeholder height="18px" width="70%" />
                            </li>
                            <li className="pass-result-card__bullet-skeleton">
                                <Placeholder height="18px" width="90%" />
                            </li>
                        </ul>
                    </>
                ) : (
                    <>
                        <Heading
                            appearance="h6"
                            headingLevel={4}
                            className="pass-result-card__title"
                        >
                            {title}
                        </Heading>
                        <ul className="pass-result-card__bullets">
                            {pass.atAGlance
                                .filter((item) => !item.text.includes('Covers entrance'))
                                .slice(0, 3)
                                .map((item) => (
                                    <li key={item.text}>
                                        <Icon
                                            iconName="check"
                                            className="pass-result-card__icon--check"
                                            size="sm"
                                        />
                                        {item.text}
                                    </li>
                                ))}
                            {providesDiscounts ? (
                                <li>
                                    <Icon
                                        iconName="check"
                                        className="pass-result-card__icon--check"
                                        size="sm"
                                    />
                                    <span>
                                        Discounts some expanded amenity fees (e.g.
                                        camping)
                                        <Tooltip content={getDiscountTooltip(pass.type)}>
                                            <span className="pass-result-card__icon--info-inline">
                                                <Icon iconName="info" size="sm" />
                                            </span>
                                        </Tooltip>
                                    </span>
                                </li>
                            ) : (
                                <li>
                                    <Icon
                                        iconName="close"
                                        className="pass-result-card__icon--close"
                                        size="sm"
                                    />
                                    Does not provide discounts
                                </li>
                            )}
                        </ul>
                    </>
                )}
                <div className="pass-result-card__actions">
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button
                            appearance={addToCartEnabled ? 'subtle' : 'primary'}
                            href={passDetailsUrl}
                            aria-label={`Learn more about ${pass.title}`}
                            gaTrackingId={gaTrackingId}
                            gaTrackingValue={gaTrackingValue}
                        >
                            Learn More
                        </Button>
                        {addToCartEnabled && (
                            <Button
                                href={orderDetailsUrl}
                                onClick={handleAddToCartClick}
                                gaTrackingId={gaTrackingId}
                                gaTrackingValue={gaTrackingValue}
                            >
                                {addToCartTitle}
                            </Button>
                        )}
                    </ButtonGroup>
                </div>
            </div>
        </div>
    );
}

export default InteragencyPassCard;
